import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { MapPoint } from './MapPoint';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class MapTrack extends BaseObj {
    public ID: number;
    public __id: number;

    public Created: Date;
    public __created: Date;

    public Description: string | null;
    public __description: string | null;

    public EventID: number;
    public __eventid: number;

    public LineColor: string | null;
    public __linecolor: string | null;

    public LineWidth: number;
    public __linewidth: number;

    public Name: string | null;
    public __name: string | null;

    public OrganizerID: number;
    public __organizerid: number;

    public Points: MapPoint[];

    public RaceID: number;
    public __raceid: number;

    public readonly TrackLength: number | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        Created?: Date;
        Description?: string | null;
        EntityState?: EntityState;
        EventID?: number;
        LineColor?: string | null;
        LineWidth?: number;
        Name?: string | null;
        OrganizerID?: number;
        Points?: MapPoint[];
        RaceID?: number;
        TrackLength?: number | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<MapTrack>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('MapTrack tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.Created = values.Created ?? new Date('1970-01-01T00:00:00.000');
        this.__created = values.Created ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'Created', {
            get(): Date {
                return this.__created;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('Created', this.__created, value);
                this.__created = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Description = values.Description ?? null;
        this.__description = values.Description ?? null;
        Object.defineProperty(this, 'Description', {
            get(): string | null {
                return this.__description;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Description', this.__description, value);
                this.__description = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LineColor = values.LineColor ?? null;
        this.__linecolor = values.LineColor ?? null;
        Object.defineProperty(this, 'LineColor', {
            get(): string | null {
                return this.__linecolor;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('LineColor', this.__linecolor, value);
                this.__linecolor = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LineWidth = values.LineWidth ?? 0;
        this.__linewidth = values.LineWidth ?? 0;
        Object.defineProperty(this, 'LineWidth', {
            get(): number {
                return this.__linewidth;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('LineWidth', this.__linewidth, value);
                this.__linewidth = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? null;
        this.__name = values.Name ?? null;
        Object.defineProperty(this, 'Name', {
            get(): string | null {
                return this.__name;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OrganizerID = values.OrganizerID ?? 0;
        this.__organizerid = values.OrganizerID ?? 0;
        Object.defineProperty(this, 'OrganizerID', {
            get(): number {
                return this.__organizerid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('OrganizerID', this.__organizerid, value);
                this.__organizerid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Points = values.Points ?? [];
        this.Points = values.Points ?? [];

        this.RaceID = values.RaceID ?? 0;
        this.__raceid = values.RaceID ?? 0;
        Object.defineProperty(this, 'RaceID', {
            get(): number {
                return this.__raceid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TrackLength = values.TrackLength ?? null;
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"Created": ${'"' + formatDateIso(this.Created) + '"'}` +
            ',' +
            `"Description": ${JSON.stringify(this.Description)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"LineColor": ${JSON.stringify(this.LineColor)}` +
            ',' +
            `"LineWidth": ${JSON.stringify(this.LineWidth)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"OrganizerID": ${JSON.stringify(this.OrganizerID)}` +
            ',' +
            `"Points": [ ${this.Points.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"TrackLength": ${JSON.stringify(this.TrackLength)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of MapTrack from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        Created?: Date;
        Description?: string | null;
        EventID?: number;
        LineColor?: string | null;
        LineWidth?: number;
        Name?: string | null;
        OrganizerID?: number;
        Points?: MapPoint[];
        RaceID?: number;
        EntityState?: 'Added' | 'Unchanged';
    }): MapTrack {
        return new MapTrack(values);
    }

    public static _FromServerJson(json: any): MapTrack {
        return new MapTrack({
            ID: json.ID,
            Created: json.Created instanceof Date ? json.Created : new Date(json.Created),
            Description: json.Description ?? '',
            EventID: json.EventID,
            LineColor: json.LineColor ?? '',
            LineWidth: json.LineWidth,
            Name: json.Name ?? '',
            OrganizerID: json.OrganizerID,
            Points: json.Points?.map((x: any) => MapPoint._FromServerJson(x)),
            RaceID: json.RaceID,
            TrackLength: json.TrackLength ?? null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of MapTrack from another instance of MapTrack.
     * @param other The MapTrack you want to clone
     * @returns A cloned MapTrack
     */
    public static FromOther(other: MapTrack): MapTrack {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return MapTrack._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return MapTrack.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'Created'
            | 'Description'
            | 'EventID'
            | 'LineColor'
            | 'LineWidth'
            | 'Name'
            | 'OrganizerID'
            | 'Points'
            | 'RaceID'
            | 'TrackLength'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'MapTrack';
}
